import { $isCodeNode } from "@lexical/code-core";
import { $isLinkNode } from "@lexical/link";
import { $convertToMarkdownString, type Transformer } from "@lexical/markdown";
import { $isListItemNode, $isListNode } from "@lexical/list";
import { $isHeadingNode, $isQuoteNode } from "@lexical/rich-text";
import { $findMatchingParent, mergeRegister } from "@lexical/utils";
import {
  $getRoot,
  $getSelection,
  $isNodeSelection,
  $isRangeSelection,
  $isRootNode,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  COMMAND_PRIORITY_LOW,
  type ElementNode,
  type LexicalEditor,
  type LexicalNode,
} from "lexical";
import type { BlockId, BlockType } from "../../core/types";
import type { SelectionInfo, SelectionState } from "../../react/contract";
import { $getBlockId } from "../block-id/state";

const EMPTY: SelectionState = {
  bold: false,
  italic: false,
  strikethrough: false,
  code: false,
  link: null,
  block: null,
  expanded: false,
  hasContent: false,
  canUndo: false,
  canRedo: false,
};

const same = (a: SelectionState, b: SelectionState): boolean =>
  (Object.keys(a) as (keyof SelectionState)[]).every((key) => a[key] === b[key]);

/** The root-level block a node is in. */
const $topBlock = (node: LexicalNode): LexicalNode | null =>
  $isRootNode(node) ? null : (node.getTopLevelElement() ?? node);

function $blockType(node: LexicalNode): BlockType {
  const item = $findMatchingParent(node, $isListItemNode);
  if (item !== null) {
    if (item.getChecked() !== undefined) return "check";
    const list = item.getParent();
    if ($isListNode(list)) return list.getListType() === "number" ? "number" : "bullet";
  }
  const block = $topBlock(node);
  if ($isHeadingNode(block)) return block.getTag();
  if ($isQuoteNode(block)) return "quote";
  if ($isCodeNode(block)) return "code";
  return "paragraph";
}

function $readState(history: { canUndo: boolean; canRedo: boolean }): SelectionState {
  const selection = $getSelection();
  const hasContent = $getRoot().getTextContent().trim() !== "";
  if (!$isRangeSelection(selection)) return { ...EMPTY, ...history, hasContent };
  const link = $findMatchingParent(selection.anchor.getNode(), $isLinkNode);
  return {
    bold: selection.hasFormat("bold"),
    italic: selection.hasFormat("italic"),
    strikethrough: selection.hasFormat("strikethrough"),
    code: selection.hasFormat("code"),
    link: $isLinkNode(link) ? link.getURL() : null,
    block: $blockType(selection.focus.getNode()),
    expanded: !selection.isCollapsed(),
    hasContent,
    ...history,
  };
}

/**
 * Tells `listener` whenever the selection state changes, and only then: typing with a caret in
 * plain text calls it once, not per keystroke.
 */
export function registerSelectionState(
  editor: LexicalEditor,
  listener: (state: SelectionState) => void,
): () => void {
  const history = { canUndo: false, canRedo: false };
  let last: SelectionState | null = null;
  // Undo and redo availability is read a microtask later, which can be after the editor is gone.
  let disposed = false;
  const refresh = (): void => {
    if (disposed) return;
    const next = editor.getEditorState().read(() => $readState(history));
    if (last !== null && same(last, next)) return;
    last = next;
    listener(next);
  };
  refresh();
  return mergeRegister(
    () => {
      disposed = true;
    },
    editor.registerUpdateListener(refresh),
    editor.registerCommand(
      CAN_UNDO_COMMAND,
      (can) => {
        history.canUndo = can;
        queueMicrotask(refresh);
        return false;
      },
      COMMAND_PRIORITY_LOW,
    ),
    editor.registerCommand(
      CAN_REDO_COMMAND,
      (can) => {
        history.canRedo = can;
        queueMicrotask(refresh);
        return false;
      },
      COMMAND_PRIORITY_LOW,
    ),
  );
}

/** The selection, read from the live editor. `null` when there is none. */
export function readSelectionInfo(
  editor: LexicalEditor,
  transformers: Transformer[],
  selectedBlockIds: readonly BlockId[],
): SelectionInfo | null {
  const found = editor.getEditorState().read(() => {
    const selection = $getSelection();
    let blocks: LexicalNode[];
    let text = "";
    let line = "";
    if (selectedBlockIds.length > 0) {
      const wanted = new Set(selectedBlockIds);
      blocks = $getRoot()
        .getChildren()
        .filter((block) => wanted.has($getBlockId(block) ?? ""));
      text = blocks.map((block) => block.getTextContent()).join("\n");
    } else if ($isRangeSelection(selection) || $isNodeSelection(selection)) {
      const seen = new Set<string>();
      blocks = [];
      for (const node of selection.getNodes()) {
        const block = $topBlock(node);
        if (block === null || seen.has(block.getKey())) continue;
        seen.add(block.getKey());
        blocks.push(block);
      }
      // `getNodes` leaves out the block a caret sits in when it is empty.
      if ($isRangeSelection(selection)) {
        const focus = $topBlock(selection.focus.getNode());
        if (focus !== null && !seen.has(focus.getKey())) blocks.push(focus);
        text = selection.getTextContent();
        const item = $findMatchingParent(selection.focus.getNode(), $isListItemNode);
        line = (item ?? focus)?.getTextContent() ?? "";
      }
      blocks.sort((a, b) => (a.isBefore(b) ? -1 : 1));
    } else {
      return null;
    }
    return { blocks: blocks.map((block) => block.getKey()), text, line };
  });
  if (found === null) return null;

  const state = editor.getEditorState();
  const { markdown, blockIds } = state.read(() => {
    const byKey = new Map(
      $getRoot()
        .getChildren()
        .map((block) => [block.getKey(), block]),
    );
    const blocks = found.blocks
      .map((key) => byKey.get(key))
      .filter((block): block is LexicalNode => block !== undefined);
    return {
      markdown: exportBlocks(blocks, transformers),
      blockIds: blocks.map($getBlockId).filter((id): id is BlockId => id !== null),
    };
  });
  return { text: found.text, line: found.line, blockIds, markdown, rect: caretRect(editor) };
}

/**
 * Root-level blocks as Markdown, written exactly as a whole-document export writes them, blank
 * lines between blocks included. In 0.51, `$convertToMarkdownString` reads nothing of the node it is
 * given but `getChildren()` (see `MarkdownExport.ts`), so the blocks are handed over under a stand-in
 * parent and their real parent is never touched.
 */
function exportBlocks(blocks: readonly LexicalNode[], transformers: Transformer[]): string {
  const parent = { getChildren: () => blocks } as unknown as ElementNode;
  return $convertToMarkdownString(transformers, parent);
}

function caretRect(editor: LexicalEditor): SelectionInfo["rect"] {
  const root = editor.getRootElement();
  const selection = root?.ownerDocument.defaultView?.getSelection();
  if (
    root === null ||
    selection === null ||
    selection === undefined ||
    selection.rangeCount === 0
  ) {
    return null;
  }
  const range = selection.getRangeAt(0);
  if (!root.contains(range.startContainer)) return null;
  let rect = range.getBoundingClientRect();
  // A caret in an empty line measures as nothing; its block does not.
  if (rect.width === 0 && rect.height === 0) {
    const element =
      range.startContainer instanceof Element
        ? range.startContainer
        : range.startContainer.parentElement;
    if (element !== null) rect = element.getBoundingClientRect();
  }
  return { left: rect.left, top: rect.top, bottom: rect.bottom };
}
